import * as React from "react"
import * as moment from "moment"
import {Button, Grid, Loader, Responsive} from "semantic-ui-react"
import {
  AirportStopFragment,
  BookingPaidBy,
  Bookings,
  BookingsResultFragment,
  BookingsSeek,
  BookingsSeekFragment,
  BookingState,
  CommonPlaces,
  PageDirection,
  PlaceCategory,
  PlaceFragment,
  PlaceNicknames,
  PlaceType,
} from "../graphql/queries"
import {momentBookingDateToBackendString} from "../model/time"
import {CommonPlace, PlaceNickname} from "../model/places/PlaceTypes"
import {maybePlaceNickname, placeCategory, placeName} from "../model/places/place"
import {BookingsListCards} from "./BookingsListCards"
import {BookingsListTableAdmin} from "./BookingsListTableAdmin"
import {ID} from "../model/id"
import {BookingListFilter} from "./BookingsList"
import {isReady, liftEitherInOption, loading, Loading, loadingIfNull, ready} from "../tsutil"
import {errorUnionToEither, handleError} from "../graphql/errors"
import {history} from "../App"
import {GraphQL} from "../graphql"

export interface BookingListTableProps {
  isAdmin: boolean
  isDriver: boolean
  filter: BookingListFilter
  seek: BookingsSeek | null
  onSeek: (seek: BookingsSeek) => void
}

// Needed to remove the __typename property. The server will bark if it's there.
const bookingsSeekOutToBookingsSeekIn = ({
  direction,
  bookingDateTime,
  bookingId,
}: BookingsSeekFragment): BookingsSeek => ({direction, bookingDateTime, bookingId})

export interface PlaceItem {
  placeName: string
  nickname: PlaceNickname | null
  type: PlaceType
  category: PlaceCategory | null
}

export interface BookingItem {
  id: ID
  bookingNumber: string
  bookingStamp: moment.Moment
  state: BookingState | null
  nettPrice: number | null
  paidBy: BookingPaidBy | null
  isImminent: boolean
  isComplete: boolean
  isCancelled: boolean
  driver: {
    id: ID
    name: string
  } | null
  isConfirmed: boolean | null
  origin: PlaceItem
  destination: PlaceItem
  airportPickup: AirportStopFragment | null
  airportDropoff: AirportStopFragment | null
  passenger: {
    name: string
  }
  booker: {
    email: string
    name: string
  } | null
}

export interface BookingListProps {
  bookingItems: Array<BookingItem>
  onBooking: (bookingNumber: string) => void
}

class BookingsListTable extends React.Component<Props, {}> {
  bookingItems(): Loading<Array<BookingItem>> {
    const {isAdmin, commonPlaces, placeNicknames, bookingsData} = this.props

    if (!isReady(placeNicknames) || !isReady(commonPlaces) || !isReady(bookingsData)) {
      return loading
    }

    const {bookings} = bookingsData.value

    const makePlaceItem = (place: PlaceFragment): PlaceItem => ({
      placeName: placeName(commonPlaces.value, place, isAdmin),
      nickname: maybePlaceNickname(placeNicknames.value, place.id) || null,
      type: place.type,
      category: placeCategory(commonPlaces.value, place),
    })

    return ready(
      bookings.map(
        ({
          id,
          number: bookingNumber,
          bookingDateTime,
          state,
          nettPrice,
          paidBy,
          isImminent,
          isComplete,
          isCancelled,
          driver,
          isConfirmed,
          journey: {origin, destination},
          airportPickup,
          airportDropoff,
          passengers,
          booker,
        }) => ({
          id,
          bookingNumber,
          bookingStamp: moment(bookingDateTime),
          state,
          nettPrice: isAdmin ? nettPrice : null,
          paidBy: isAdmin ? paidBy : null,
          isImminent,
          isComplete,
          isCancelled,
          driver,
          isConfirmed,
          origin: makePlaceItem(origin),
          destination: makePlaceItem(destination),
          airportPickup,
          airportDropoff,
          passenger: {
            name: passengers[0].name,
          },
          booker: booker
            ? {
                email: booker.email,
                name: booker.name,
              }
            : null,
        }),
      ),
    )
  }

  render() {
    const {isAdmin, bookingsData, bookingsLoading, onSeek} = this.props
    const bookingItems = this.bookingItems()

    if (!isReady(bookingsData) || !isReady(bookingItems) || bookingsLoading) {
      return <Loader active />
    }

    const {next, prev} = bookingsData.value
    const someBookings = bookingItems.value.length > 0

    return (
      <Grid>
        <Grid.Row>
          <Grid.Column>
            {!someBookings && <div>{`There are no matching bookings`}</div>}
            {someBookings &&
              (isAdmin ? (
                <div>
                  <Responsive
                    as={BookingsListTableAdmin}
                    minWidth={1024}
                    bookingItems={bookingItems.value}
                    onBooking={onBooking}
                  />
                  <Responsive
                    as={BookingsListCards}
                    maxWidth={1023}
                    bookingItems={bookingItems.value}
                    onBooking={onBooking}
                  />
                </div>
              ) : (
                <BookingsListCards bookingItems={bookingItems.value} onBooking={onBooking} />
              ))}
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <Button.Group>
              <Button
                size="small"
                disabled={prev == null}
                onClick={() => {
                  if (prev != null) {
                    onSeek(bookingsSeekOutToBookingsSeekIn(prev))
                  }
                }}
                content="Prev"
              />
              <Button
                size="small"
                disabled={next == null}
                onClick={() => {
                  if (next != null) {
                    onSeek(bookingsSeekOutToBookingsSeekIn(next))
                  }
                }}
                content="Next"
              />
            </Button.Group>
          </Grid.Column>
        </Grid.Row>
      </Grid>
    )
  }
}

function onBooking(bookingNumber: string) {
  history.push("/booking/" + bookingNumber)
}

type Props = CommonPlacesProps & {placeNicknames: Loading<Array<PlaceNickname>>}

function WithPlaceNicknames(props: CommonPlacesProps) {
  return (
    <GraphQL.Query
      query={PlaceNicknames}
      variables={{}}
      render={({data}) => {
        return (
          <BookingsListTable
            {...props}
            placeNicknames={loadingIfNull(data).map((data) => data.placeNicknames)}
          />
        )
      }}
    />
  )
}

type CommonPlacesProps = BookingsProps & {commonPlaces: Loading<Array<CommonPlace>>}

function WithCommonPlaces(props: BookingsProps) {
  return (
    <GraphQL.Query
      query={CommonPlaces}
      variables={{}}
      render={({data}) => {
        return (
          <WithPlaceNicknames
            {...props}
            commonPlaces={loadingIfNull(data).map((data) => data.commonPlaces)}
          />
        )
      }}
    />
  )
}

type BookingsProps = BookingListTableProps & {
  bookingsData: Loading<BookingsResultFragment>
  bookingsLoading: boolean
}

function WithBookings(props: BookingListTableProps) {
  const {filter, seek, isAdmin, isDriver} = props
  return (
    <GraphQL.Query
      query={Bookings}
      variables={{
        filter: {
          state: filter.bookingsState,
          bookerId: filter.booker && filter.booker.id,
          driverId: filter.driver && filter.driver.id,
        },
        seek: seek || {
          bookingDateTime: momentBookingDateToBackendString(filter.fromDate) + "T00:00:00",
          direction: PageDirection.After,
        },
      }}
      fetchPolicy="cache-and-network"
      pollInterval={isAdmin || isDriver ? 120 * 1000 : 0}
      render={({data, loading}) => {
        const bookings = liftEitherInOption(
          loadingIfNull(data).map((x) => errorUnionToEither(x.bookings)),
        )
        return handleError(bookings, (bookings) => (
          <WithCommonPlaces {...props} bookingsData={bookings} bookingsLoading={loading} />
        ))
      }}
    />
  )
}

export {WithBookings as BookingsListTable}



// WEBPACK FOOTER //
// ./src/components/BookingsListTable.tsx